import { useState } from "react";
import {
  Radio,
  Users,
  Zap,
  TrendingUp,
  Gavel,
  Gift,
  ShoppingBag,
  MessageSquare,
  Trophy,
  Crown,
  Star,
  ArrowRight,
  Sparkles,
  Play,
  Heart,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { StreamMock } from "@/components/StreamMock";
import { WaitlistModal } from "@/components/WaitlistModal";
import seller1 from "@/assets/seller-1.jpg";
import seller2 from "@/assets/seller-2.jpg";
import seller3 from "@/assets/seller-3.jpg";

export function Landing() {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<"buyer" | "seller">("buyer");

  const openModal = (r: "buyer" | "seller" = "buyer") => {
    setRole(r);
    setOpen(true);
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Navbar onOpen={() => openModal("buyer")} />
      <Toaster theme="dark" position="top-center" />

      {/* HERO */}
      <section className="relative px-4 pb-24 pt-32 md:pt-40">
        <div className="pointer-events-none absolute left-1/4 top-20 -z-10 h-96 w-96 rounded-full bg-primary/30 blur-[120px]" />
        <div className="pointer-events-none absolute right-1/4 top-60 -z-10 h-96 w-96 rounded-full bg-amber/15 blur-[140px]" />

        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="animate-fade-in-up">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-surface-1/60 px-3 py-1.5 backdrop-blur">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-pulse-live rounded-full bg-live opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-live" />
              </span>
              <span className="text-xs font-semibold tracking-wider">LIVE COMMERCE · NOVA ERA</span>
            </div>

            <h1 className="text-5xl font-extrabold leading-[1.02] tracking-tight md:text-7xl lg:text-[5.5rem]">
              Compras ao Vivo.
              <br />
              <span className="text-gradient">Energia em Tempo Real.</span>
            </h1>

            <p className="mt-6 max-w-xl text-lg text-muted-foreground md:text-xl">
              Transmita. Dê lances. Reaja. Ganhe. A arena onde vendedores entram ao vivo,
              o público interage em tempo real e cada drop vira um evento.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => openModal("buyer")}
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-primary px-6 py-3.5 text-sm font-bold text-primary-foreground shadow-elegant transition-transform hover:scale-105"
              >
                Entrar na Lista
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => openModal("seller")}
                className="inline-flex items-center gap-2 rounded-full bg-surface-1/70 px-6 py-3.5 text-sm font-bold text-foreground backdrop-blur transition-colors hover:bg-surface-2"
              >
                <Play className="h-4 w-4 fill-current" />
                Comece a Vender
              </button>
            </div>

            <div className="mt-10 flex items-center gap-6 text-xs text-muted-foreground">
              <div>
                <div className="text-2xl font-extrabold text-foreground">12K+</div>
                <div>streamers ativos</div>
              </div>
              <div className="h-8 w-px bg-border" />
              <div>
                <div className="text-2xl font-extrabold text-foreground">R$ 21M</div>
                <div>GMV mensal</div>
              </div>
              <div className="h-8 w-px bg-border" />
              <div>
                <div className="text-2xl font-extrabold text-foreground">98%</div>
                <div>retenção da live</div>
              </div>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end">
            <StreamMock />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative px-4 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 max-w-2xl">
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary-glow">
              Como funciona
            </div>
            <h2 className="text-4xl font-extrabold tracking-tight md:text-5xl">
              Quatro passos do <span className="text-gradient">holofote à venda</span>
            </h2>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              { i: Radio, t: "Vá ao Vivo", d: "Inicie sua transmissão em segundos. Sem estúdio. Sem complicação." },
              { i: Users, t: "Engaje o Público", d: "Chat, reações, enquetes — transforme espectadores em fãs." },
              { i: Zap, t: "Venda em Tempo Real", d: "Leilões, drops, compra instantânea. Toque para converter." },
              { i: TrendingUp, t: "Lucre & Cresça", d: "Suba no ranking. Desbloqueie novos públicos." },
            ].map((s, i) => (
              <div
                key={i}
                className={`relative overflow-hidden rounded-3xl bg-surface-1/60 p-6 backdrop-blur transition-transform hover:-translate-y-1 ${
                  i % 2 === 1 ? "md:translate-y-8" : ""
                }`}
              >
                <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-primary/15 blur-2xl" />
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-primary shadow-glow">
                  <s.i className="h-5 w-5 text-primary-foreground" />
                </div>
                <div className="mb-1 text-xs font-bold text-muted-foreground">0{i + 1}</div>
                <h3 className="mb-2 text-xl font-extrabold">{s.t}</h3>
                <p className="text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE EXPERIENCE */}
      <section id="live" className="relative px-4 py-24">
        <div className="pointer-events-none absolute inset-x-0 top-1/2 -z-10 h-[500px] -translate-y-1/2 bg-gradient-to-r from-primary/10 via-transparent to-amber/10 blur-3xl" />

        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-16 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <div className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary-glow">
                A arena
              </div>
              <h2 className="text-4xl font-extrabold tracking-tight md:text-5xl">
                Feita para momentos que <span className="text-gradient">não podem ser pausados</span>
              </h2>
              <p className="mt-5 text-lg text-muted-foreground">
                Disputas de lances ao vivo. Tempestades de corações. Surtos de comentários.
                Cada camada da experiência pulsa com a sala.
              </p>

              <div className="mt-8 space-y-4">
                {[
                  { i: Gavel, t: "Leilões ao Vivo", d: "Dê lances em tempo real." },
                  { i: MessageSquare, t: "Chat Instantâneo", d: "Conversas que movem o preço." },
                  { i: Heart, t: "Tempestade de Reações", d: "Energia tornada visível." },
                  { i: Trophy, t: "Drops Gamificados", d: "Ganhe, evolua, desbloqueie perks." },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="rounded-xl bg-surface-2/80 p-2.5">
                      <f.i className="h-4 w-4 text-primary-glow" />
                    </div>
                    <div>
                      <div className="font-bold">{f.t}</div>
                      <div className="text-sm text-muted-foreground">{f.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative h-[520px]">
              {[
                { src: seller1, name: "@sneaker.lab", item: "Air Force 1 Custom", price: "R$ 2.100", rot: "-rotate-6", pos: "left-0 top-8", v: "8,2K" },
                { src: seller3, name: "@chrono", item: "Diver Vintage", price: "R$ 9.200", rot: "rotate-3", pos: "right-0 top-0", v: "3,1K" },
                { src: seller2, name: "@vela.studio", item: "Mini Matelassê", price: "R$ 3.400", rot: "-rotate-3", pos: "left-16 bottom-0", v: "5,6K" },
              ].map((s, i) => (
                <div
                  key={i}
                  className={`absolute aspect-[9/12] w-56 overflow-hidden rounded-2xl shadow-deep transition-transform hover:scale-105 hover:rotate-0 ${s.rot} ${s.pos}`}
                >
                  <img src={s.src} alt={s.name} className="h-full w-full object-cover" loading="lazy" width={800} height={1000} />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/95" />
                  <div className="absolute left-3 top-3 glass flex items-center gap-1.5 rounded-full px-2 py-1">
                    <span className="h-1.5 w-1.5 animate-pulse-live rounded-full bg-live" />
                    <span className="text-[10px] font-bold tracking-wider">{s.v}</span>
                  </div>
                  <div className="absolute inset-x-3 bottom-3">
                    <div className="text-[10px] font-bold text-primary-glow">{s.name}</div>
                    <div className="text-sm font-extrabold leading-tight">{s.item}</div>
                    <div className="mt-1 inline-block rounded-md bg-gradient-primary px-2 py-0.5 text-xs font-extrabold text-primary-foreground">
                      {s.price}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SELLERS / BUYERS SPLIT */}
      <section id="sellers" className="relative px-4 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 text-center">
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary-glow">
              Dois lados. Uma arena.
            </div>
            <h2 className="text-4xl font-extrabold tracking-tight md:text-5xl">
              De qualquer lado, você <span className="text-gradient">ganha</span>
            </h2>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Sellers */}
            <div className="relative overflow-hidden rounded-3xl bg-surface-1/60 p-8 backdrop-blur">
              <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-primary/30 blur-3xl" />
              <div className="relative">
                <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-primary-glow">
                  Para Vendedores
                </div>
                <h3 className="text-3xl font-extrabold">Monetize cada minuto</h3>
                <p className="mt-3 text-muted-foreground">
                  Transforme seguidores em compradores com ferramentas feitas para conversão ao vivo.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    { i: Users, t: "Monetize sua audiência" },
                    { i: Gavel, t: "Leilões e quedas de preço ao vivo" },
                    { i: Sparkles, t: "Drops de produtos limitados" },
                    { i: TrendingUp, t: "Análises de engajamento" },
                  ].map((b, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="rounded-lg bg-primary/20 p-1.5">
                        <b.i className="h-3.5 w-3.5 text-primary-glow" />
                      </div>
                      <span className="text-sm font-medium">{b.t}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => openModal("seller")}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-primary px-5 py-3 text-sm font-bold text-primary-foreground shadow-elegant transition-transform hover:scale-105"
                >
                  Comece a Vender <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Buyers */}
            <div className="relative overflow-hidden rounded-3xl bg-surface-1/60 p-8 backdrop-blur lg:translate-y-8">
              <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-amber/25 blur-3xl" />
              <div className="relative">
                <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-amber">
                  Para Compradores
                </div>
                <h3 className="text-3xl font-extrabold">Esteja na sala</h3>
                <p className="mt-3 text-muted-foreground">
                  Drops, ofertas e momentos que não existem em nenhum outro lugar.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    { i: Gift, t: "Drops e sorteios exclusivos" },
                    { i: ShoppingBag, t: "Lances ao vivo e compra instantânea" },
                    { i: MessageSquare, t: "Fale com vendedores em tempo real" },
                    { i: Star, t: "Vantagens só para a comunidade" },
                  ].map((b, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className="rounded-lg bg-amber/15 p-1.5">
                        <b.i className="h-3.5 w-3.5 text-amber" />
                      </div>
                      <span className="text-sm font-medium">{b.t}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => openModal("buyer")}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-surface-2/80 px-5 py-3 text-sm font-bold text-foreground transition-colors hover:bg-surface-3"
                >
                  Entrar como Comprador <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GAMIFICATION */}
      <section id="rewards" className="relative px-4 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr]">
            <div>
              <div className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary-glow">
                Gamificação
              </div>
              <h2 className="text-4xl font-extrabold tracking-tight md:text-5xl">
                Cada ação <span className="text-gradient">te faz evoluir</span>
              </h2>
              <p className="mt-5 text-lg text-muted-foreground">
                Sequências, ranks, recompensas. Quanto mais você aparece, mais a arena devolve —
                para vendedores e compradores.
              </p>
              <div className="mt-8 grid grid-cols-3 gap-3">
                {[
                  { v: "Nv. 42", l: "Vendedor médio" },
                  { v: "3,2x", l: "Engajamento" },
                  { v: "27", l: "Recompensas" },
                ].map((s, i) => (
                  <div key={i} className="rounded-2xl bg-surface-1/70 p-4 backdrop-blur">
                    <div className="text-2xl font-extrabold text-gradient">{s.v}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-strong relative rounded-3xl p-6 shadow-deep">
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-amber" />
                  <span className="font-extrabold">Ranking ao Vivo</span>
                </div>
                <span className="text-xs text-muted-foreground">Esta semana</span>
              </div>
              <div className="space-y-2.5">
                {[
                  { r: 1, n: "@sneaker.lab", p: "12.840", img: seller1, glow: true },
                  { r: 2, n: "@vela.studio", p: "9.620", img: seller2 },
                  { r: 3, n: "@chrono", p: "7.205", img: seller3 },
                  { r: 4, n: "@drop.zone", p: "5.890" },
                  { r: 5, n: "@hauteline", p: "4.210" },
                ].map((p) => (
                  <div
                    key={p.r}
                    className={`flex items-center gap-3 rounded-2xl p-3 transition-colors ${
                      p.glow ? "bg-gradient-primary/20 shadow-glow" : "bg-surface-1/60 hover:bg-surface-2/60"
                    }`}
                  >
                    <div
                      className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-extrabold ${
                        p.r === 1 ? "bg-amber text-background" : "bg-surface-2"
                      }`}
                    >
                      {p.r}
                    </div>
                    {p.img ? (
                      <img src={p.img} alt={p.n} className="h-9 w-9 rounded-full object-cover" loading="lazy" width={40} height={40} />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-surface-2" />
                    )}
                    <div className="flex-1">
                      <div className="text-sm font-bold">{p.n}</div>
                      <div className="text-[10px] text-muted-foreground">{p.p} pts</div>
                    </div>
                    {p.r === 1 && <Trophy className="h-4 w-4 text-amber" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative px-4 py-32">
        <div className="pointer-events-none absolute inset-0 -z-10 mx-auto max-w-5xl">
          <div className="absolute inset-0 rounded-[3rem] bg-gradient-primary opacity-20 blur-3xl" />
        </div>
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-surface-1/60 px-3 py-1.5 backdrop-blur">
            <Sparkles className="h-3 w-3 text-amber" />
            <span className="text-xs font-semibold tracking-wider">ACESSO ANTECIPADO — VAGAS LIMITADAS</span>
          </div>
          <h2 className="text-5xl font-extrabold tracking-tight md:text-7xl">
            Entre na <span className="text-gradient">arena.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
            A próxima era do comércio não é navegada. É vivida. Esteja entre os primeiros a garantir seu lugar.
          </p>
          <button
            onClick={() => openModal("buyer")}
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-gradient-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-elegant transition-transform hover:scale-105"
          >
            Entrar na Lista
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      <footer className="border-t border-border/40 px-4 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 text-sm text-muted-foreground md:flex-row">
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-gradient-primary p-1">
              <Radio className="h-3 w-3 text-primary-foreground" />
            </div>
            <span className="font-extrabold text-foreground">InLiveShop</span>
            <span>© 2026</span>
          </div>
          <div>Transforme compras em uma experiência ao vivo.</div>
        </div>
      </footer>

      <WaitlistModal open={open} onClose={() => setOpen(false)} defaultRole={role} />
    </div>
  );
}
